import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ListScaffold } from "@/components/layout/ListScaffold";
import {
  Badge,
  Input,
  PageHeader,
  Select,
  Table,
  TableWrap,
  TD,
  TH,
  THead,
  TR,
} from "@/components/ui";
import { useApi } from "@/lib/api";
import { ago, count, decimal, personLabel } from "@/lib/format";
import { useDebounced } from "./useDebounced";

type Filter = "all" | "flagged" | "deleted" | "withPhoto";

export function ReviewsScreen() {
  const api = useApi();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const search = useDebounced(q, 250);

  const reviews = useQuery({
    queryKey: ["admin", "reviews", { search, filter }],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/reviews", {
        params: { query: { q: search || undefined, filter, limit: 50 } },
      });
      if (error) throw error;
      return data;
    },
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        eyebrow="Corpus"
        title="Reviews"
        description="Taking a review down is a soft delete — the score recomputes without it and restoring puts it back exactly as it was."
      />
      <ListScaffold
        filters={
          <>
            <Input
              placeholder="Search note, cafe or handle"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-[260px]"
            />
            <Select
              value={filter}
              onChange={(e) => setFilter(e.target.value as Filter)}
            >
              <option value="all">All live reviews</option>
              <option value="flagged">Flagged (open report)</option>
              <option value="withPhoto">With a photo</option>
              <option value="deleted">Taken down</option>
            </Select>
          </>
        }
        isLoading={reviews.isLoading}
        isError={reviews.isError}
        onRetry={() => void reviews.refetch()}
        isEmpty={(reviews.data?.items.length ?? 0) === 0}
        emptyTitle={
          filter === "flagged"
            ? "Nothing is flagged"
            : filter === "deleted"
              ? "Nothing has been taken down"
              : "No reviews match that"
        }
        emptyHint={
          filter === "flagged"
            ? "Reports land here the moment somebody files one."
            : undefined
        }
      >
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>Review</TH>
                <TH>Cafe</TH>
                <TH>Person</TH>
                <TH align="right">Matcha</TH>
                <TH align="right">Room</TH>
                <TH align="right">Photos</TH>
                <TH>State</TH>
                <TH>Posted</TH>
              </TR>
            </THead>
            <tbody>
              {reviews.data?.items.map((review) => (
                <TR
                  key={review.id}
                  onClick={() => navigate(`/reviews/${review.id}`)}
                >
                  <TD className="max-w-[320px]">
                    <span className="block truncate text-ink-2">
                      {review.note ?? (
                        <span className="text-muted-2">no note</span>
                      )}
                    </span>
                  </TD>
                  <TD muted>{review.cafe?.name ?? "—"}</TD>
                  <TD>{personLabel(review.user)}</TD>
                  <TD align="right" mono>
                    {decimal(review.rating)}
                  </TD>
                  <TD align="right" mono>
                    {decimal(review.roomRating)}
                  </TD>
                  <TD align="right" mono>
                    {count(review.photoCount)}
                  </TD>
                  <TD>
                    <span className="flex gap-1">
                      {review.openReportCount > 0 ? (
                        <Badge tone="danger">
                          {review.openReportCount} report
                          {review.openReportCount === 1 ? "" : "s"}
                        </Badge>
                      ) : null}
                      {review.deletedAt ? (
                        <Badge tone="neutral">taken down</Badge>
                      ) : null}
                      {review.visibility !== "public" ? (
                        <Badge tone="neutral">{review.visibility}</Badge>
                      ) : null}
                    </span>
                  </TD>
                  <TD muted>{ago(review.createdAt)}</TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </ListScaffold>
    </div>
  );
}
